import ListViewViewModel from '@/ViewModels/Views/ListViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import CustomDataGridViewModel from '@/ViewModels/Controls/CustomDataGridViewModel';
import * as CoreFrameDTOs from "coreframetypings/dto";
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import ResourceManager from '@/Management/ResourceManager';
import Messenger from '@/Utility/Messenger';
import NavigationService from '@/Management/NavigationService';
import ListViewDataGridHelper from '@/Components/ListView/Helper/ListViewDataGridHelper';
import {
    ICustomDataGridViewModelConstructorParameter,
    ICustomListViewViewModelParameters
} from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';
import DataGridViewModel from '@/ViewModels/Controls/DataGridViewModel';

export default class CustomListViewViewModel extends ListViewViewModel {

    private readonly dataSourceTypeName: string;
    private readonly dataSourceOperationName: string;
    private readonly pageTitleErgName: string;
    private readonly onBeforeColumnsComputedHandler: (columns: Array<any>) => void;
    private readonly onBeforeListViewLoadedHandler: (listViewDTO: CoreFrameDTOs.IListViewDTO) => void;
    private readonly exportDataHandler: (exportType: any) => void;
    private readonly key: string;
    private readonly customName: string;

    /**
     * @override
     */
    protected listViewDataGridHelper: ListViewDataGridHelper<CustomDataGridViewModel, ICustomDataGridViewModelConstructorParameter>;

    constructor(params: ICustomListViewViewModelParameters) {
        super($.extend(params, { isInherited: true }));

        this.dataSourceTypeName = params.dataSourceTypeName;
        this.dataSourceOperationName = params.dataSourceOperationName;

        this.pageTitleErgName = this.paramOrDefault(params.pageTitleErgName, '');
        this.key = this.paramOrDefault(params.key, this.pageTitleErgName);
        this.customName = this.paramOrDefault(params.customName, 'CustomList');

        this.onBeforeColumnsComputedHandler = params.onBeforeColumnsComputedHandler;
        this.onBeforeListViewLoadedHandler = params.onBeforeListViewLoadedHandler;
        this.exportDataHandler = params.exportDataHandler;

        this.loadListView();
    }

    protected shouldUseCustomDataLoading(): boolean {
        return Utilities.isNotBlank(this.dataSourceTypeName) && Utilities.isNotBlank(this.dataSourceOperationName);
    }

    /**
     * @override
     */
    public getPageTitle(): string {
        return ResourceManager.getInstance().get(this.pageTitleErgName);
    }

    /**
     * @override
     */
    public getPageTitlePrefix(): string {
        return '';
    }

    /**
     * @override
     */
    public createPageTitleLabel(dto: CoreFrameDTOs.IListViewDTO): LabelViewModel {
        return new LabelViewModel({
            ergName: this.pageTitleErgName
        });
    }

    /**
     * @override
     */
    public sendEnteredMessage(): void {
        Messenger.createMessageAndSend('ExtensionViewEnteredMessage',
            {
                context: this,
                title: this.getPageTitle(),
                key: this.key,
            });
    }

    /**
     * @override
     */
    public sendExitedMessage(): void {
        Messenger.createMessageAndSend('ExtensionViewExitedMessage',
            {
                context: this
            });
    }

    /**
     * @override
     */
    protected getStoragePrincipalFilterModeKey(): string {
        return `${this.customName}_PRINCIPAL_FILTER_MODE`;
    }

    /**
     * @override
     */
    public getStorageColumnDefinitionKey(listViewDto: CoreFrameDTOs.IListViewDTO) {
        return listViewDto.columns.length > 0 ? this.customName + '_ColumnDefinition' : null;
    }

    /**
     * @override
     */
    protected getListViewDataGridHelper(listView: CoreFrameDTOs.IListViewDTO): ListViewDataGridHelper<DataGridViewModel, ICustomDataGridViewModelConstructorParameter> {
        if (this.listViewDataGridHelper == null) {
            this.listViewDataGridHelper = new ListViewDataGridHelper<CustomDataGridViewModel, ICustomDataGridViewModelConstructorParameter>(this, listView);
            this.listViewDataGridHelper.setStorageName(this.customName);
            this.listViewDataGridHelper.setListViewPrefix(this.customName);
            if (this.shouldUseCustomDataLoading()) { 
                this.listViewDataGridHelper.setDataGridImplementation(ComponentManager.getViewModel<typeof CustomDataGridViewModel>('CustomDataGridViewModel'));
            }
        }

        return this.listViewDataGridHelper;
    }

    /**
     * @override
     */
    public createDataGrid(listView: CoreFrameDTOs.IListViewDTO): void {
        const parameters = this.createDataGridViewModelParameters(listView);

        const helper = this.getListViewDataGridHelper(listView);
        this.dataGrid = helper.createDataGridViewModel(parameters);
        this.dataGrid.init();
    }

    /**
     * @override
     */
    protected createDataGridViewModelParameters(listView: CoreFrameDTOs.IListViewDTO): ICustomDataGridViewModelConstructorParameter {
        if (!this.shouldUseCustomDataLoading()) {
            return super.createDataGridViewModelParameters(listView);
        }

        const helper = this.getListViewDataGridHelper(listView);
        helper.setAdditionalFilterConstructorParameter({
            preFilterTooltip: this.preFilterTooltip
        });
        helper.setAdditionalDataGridConstructorParameters({
            dataSourceTypeName: this.dataSourceTypeName,
            dataSourceOperationName: this.dataSourceOperationName,
            rowLimit: listView.rowLimit,
            onBeforeColumnsComputedHandler: this.onBeforeColumnsComputedHandler,
            onDoubleClickHandler: (pdoId, row, openInNewWindow) => this.onDoubleClick(pdoId, row, openInNewWindow)
        });

        return helper.createDataGridViewModelParameters();
    }

    protected onDoubleClick(pdoId: string, row: any, openInNewWindow: boolean): void {
        const btn = this.toolBar.getElementByErgName('CREATE_EDIT');
        if (btn && btn.isVisible() === true) {
            this.onEdit(pdoId, openInNewWindow);
        }
    }
    
    /**
     * @override
     */
    public exportData(exportType: any, showFilter = true): void {
        if (Utilities.isFunction(this.exportDataHandler))
            this.exportDataHandler(exportType);
        else
            super.exportData(exportType, showFilter);
    }

    /**
     * @override
     * @inheritdoc 
     */
    public onBeforeListViewLoaded(listViewDTO: CoreFrameDTOs.IListViewDTO): void {
        if (Utilities.isFunction(this.onBeforeListViewLoadedHandler))
            this.onBeforeListViewLoadedHandler(listViewDTO);
    }

    /**
     * @override
     * @inheritdoc 
     */
    public registerForPrincipalChangedMessage() {
        this.registerForMessageId('PrincipalChangedMessage', (message) => {
            if (!this.isDisposed) {
                NavigationService.getInstance().refresh();
            }
        });
    }
}

ComponentManager.setViewModel('CustomListViewViewModel', CustomListViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');